import { useEffect } from 'react'

import type { CartItemId } from 'models/purchaseFlowRevamp'
import { usePfMeasurementQuote } from 'dataQueries/purchaseFlowRevampMeasurement.query'

import { usePfStore } from '../../store'
import { toMeasurementRequest } from './measurement.dto'
import { useMeasurementConfig } from './useMeasurementConfig'

/**
 * Keeps the cart line's price equal to what the configuration will actually cost.
 *
 * A measurement line's catalogue price is the base carrier alone — the engine
 * derives every measured extra from the configuration, so the
 * snapshot taken when the product was added is not the price and can be less than
 * half of it. This writes the quoted net back onto the line whenever the answers
 * change, so the cart, the summary and the order agree.
 *
 * Net, not gross: every other line in the cart is net and VAT is added once at the
 * bottom, so a gross figure here would be taxed twice over.
 *
 * UNDISCOUNTED net, deliberately: `SessionPaymentService.calculateAmount` sums every
 * line's net and applies `organization.discount` ONCE to the sum, so the measurement
 * line must enter the cart on the same footing as the rest and let `CartSummary` take
 * the discount off the total (where legacy was excluding it and quoting a
 * discount of zero). The one thing that would break it is the engine stamping its own
 * `net.discountPercentage`, which backend ISSUE-3363 will add — at that point the
 * quote's discount has to be read here and reconciled with the catalogue's, exactly as
 * legacy now does in `Cart.tsx`, or a client with two different discounts is quoted the
 * wrong one.
 */
export function useMeasurementQuoteSync(cartItemId: CartItemId): void {
  const { value, isComplete } = useMeasurementConfig(cartItemId)

  const request = isComplete ? toMeasurementRequest(value) : null
  const { data: quote } = usePfMeasurementQuote(request as never)

  const net = quote?.data?.net?.value?.value
  const currency = quote?.data?.currency
  // Watched too, so a line rewritten with another price (the drawer's "Save changes") is put back.
  const linePrice = usePfStore((s) => s.items.find((i) => i.id === cartItemId)?.priceSnapshot?.unitPrice)

  useEffect(() => {
    if (!net || !currency) return

    const state = usePfStore.getState()
    const item = state.items.find((i) => i.id === cartItemId)
    if (!item) return
    // The quote re-resolves on every keystroke; only a changed amount is a write.
    if (item.priceSnapshot?.unitPrice === net && item.priceSnapshot?.currency === currency) return

    const { id: _id, ...rest } = item
    state.replaceItem(cartItemId, {
      ...rest,
      priceSnapshot: { unitPrice: net, currency, resolvedAt: new Date().toISOString() },
    })
  }, [net, currency, cartItemId, linePrice])
}
